// src/config/database/finishingProcessesConfig.js

const isPunchProcess = (formData) => (
  formData.id === 'punch' || formData.name?.trim().toLowerCase() === 'punch'
);

export const finishingProcessesTableConfig = {
  // Table identification
  id: 'sf_finishing_processes',
  name: 'Small Format Finishing Processes',
  apiEndpoint: '/api/admin/small-format/finishing-processes',
  
  // Column definitions for the DataTable
  columns: [
    {
      key: 'name',
      label: 'Process Name',
      sortable: true,
      render: (value) => (
        <span className="font-medium text-gray-900">{value}</span>
      )
    },
    {
      key: 'category',
      label: 'Category',
      sortable: true,
      render: (value) => (
        <span className="inline-flex px-2 py-1 text-xs font-medium rounded-md bg-blue-100 text-blue-800">
          {value || 'General'}
        </span>
      )
    },
    {
      key: 'description',
      label: 'Description',
      sortable: true,
      render: (value) => (
        <span className="text-gray-700 truncate max-w-xs" title={value}>
          {value || '-'}
        </span>
      )
    },
    {
      key: 'setup_cost',
      label: 'Setup Cost',
      sortable: true,
      render: (value) => (
        <span className="text-green-600 font-medium">
          {value ? `£${parseFloat(value).toFixed(2)}` : '-'}
        </span>
      )
    },
    {
      key: 'cost_per_sheet',
      label: 'Cost/Sheet',
      sortable: true,
      render: (value) => (
        <span className="text-green-600 font-medium">
          {value ? `£${parseFloat(value).toFixed(4)}` : '-'}
        </span>
      )
    },
    {
      key: 'cost_per_item',
      label: 'Cost/Item',
      sortable: true,
      render: (value) => {
        if (!value) return <span className="text-gray-400">-</span>;
        
        // Check if it's a JSON array (tiered pricing)
        if (typeof value === 'string' && value.trim().startsWith('[')) {
          try {
            const tiers = JSON.parse(value);
            if (Array.isArray(tiers) && tiers.length > 0) {
              const minCost = Math.min(...tiers.map(t => t.cost));
              const maxCost = Math.max(...tiers.map(t => t.cost));
              return (
                <div className="text-green-600 font-medium">
                  <div className="text-xs text-blue-600 mb-1">Tiered ({tiers.length} levels)</div>
                  <div>£{minCost.toFixed(4)} - £{maxCost.toFixed(4)}</div>
                </div>
              );
            }
          } catch (e) {
            console.warn('Error parsing tiered pricing:', e);
          }
        }
        
        // Handle array directly (already parsed)
        if (Array.isArray(value) && value.length > 0) {
          const minCost = Math.min(...value.map(t => t.cost));
          const maxCost = Math.max(...value.map(t => t.cost));
          return (
            <div className="text-green-600 font-medium">
              <div className="text-xs text-blue-600 mb-1">Tiered ({value.length} levels)</div>
              <div>£{minCost.toFixed(4)} - £{maxCost.toFixed(4)}</div>
            </div>
          );
        }
        
        // Handle simple numeric value
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
          return (
            <span className="text-green-600 font-medium">
              £{numValue.toFixed(4)}
            </span>
          );
        }
        
        return <span className="text-gray-400">-</span>;
      }
    },
    {
      key: 'cost_per_cut',
      label: 'Cost/Cut',
      sortable: true,
      render: (value) => (
        <span className="text-orange-600 font-medium">
          {value ? `£${parseFloat(value).toFixed(4)}` : '-'}
        </span>
      )
    },
    {
      key: 'cost_per_stack',
      label: 'Cost/Stack',
      sortable: true,
      render: (value) => (
        <span className="text-orange-600 font-medium">
          {value ? `£${parseFloat(value).toFixed(2)}` : '-'}
        </span>
      )
    },
    {
      key: 'cutting_stack_capacities',
      label: 'Stack Capacities',
      sortable: false,
      render: (value) => {
        if (!Array.isArray(value) || value.length === 0) {
          return <span className="text-gray-400">-</span>;
        }

        return (
          <span className="text-gray-700 text-xs">
            {value.map(({ max_gsm, sheets_per_stack }) => (
              `${max_gsm}gsm: ${sheets_per_stack}`
            )).join(', ')}
          </span>
        );
      }
    },
    {
      key: 'punch_stack_capacities',
      label: 'Punch Capacities',
      sortable: false,
      render: (value) => {
        if (!Array.isArray(value) || value.length === 0) {
          return <span className="text-gray-400">-</span>;
        }

        return (
          <span className="text-gray-700 text-xs">
            {value.map(({ max_gsm, sheets_per_stack }) => (
              `${max_gsm}gsm: ${sheets_per_stack}`
            )).join(', ')}
          </span>
        );
      }
    },
    {
      key: 'requires_setup',
      label: 'Requires Setup',
      sortable: true,
      render: (value) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          value ? 'bg-orange-100 text-orange-800' : 'bg-gray-100 text-gray-800'
        }`}>
          {value ? 'Yes' : 'No'}
        </span>
      )
    },
    {
      key: 'time_minutes',
      label: 'Time (min)',
      sortable: true,
      render: (value) => (
        <span className="text-gray-700">
          {value ? `${value}min` : '-'}
        </span>
      )
    },
    {
      key: 'minimum_paper_weight_gsm',
      label: 'Min Paper Weight',
      sortable: true,
      render: (value) => value == null
        ? <span className="text-gray-400">-</span>
        : <span className="text-gray-700">{value} gsm</span>
    },
    {
      key: 'available_for',
      label: 'Available For',
      sortable: false,
      render: (value) => {
        if (!value || !Array.isArray(value)) return <span className="text-gray-400">-</span>;
        
        if (value.includes('all') || value.length === 0) {
          return <span className="text-green-600">All</span>;
        }
        
        return (
          <div className="flex flex-wrap gap-1">
            {value.slice(0, 2).map((item, index) => (
              <span 
                key={index}
                className="inline-flex px-1.5 py-0.5 text-xs font-medium rounded bg-purple-100 text-purple-800"
              >
                {item}
              </span>
            ))}
            {value.length > 2 && (
              <span className="text-xs text-gray-500">+{value.length - 2} more</span>
            )}
          </div>
        );
      }
    }
  ],

  // Form field definitions for the RecordModal
  fields: [
    {
      key: 'name',
      label: 'Process Name',
      type: 'text',
      required: true,
      placeholder: 'e.g., Saddle Stitching, Perfect Binding, Laminating',
      help: 'Descriptive name for this finishing process'
    },
    {
      key: 'description',
      label: 'Description',
      type: 'textarea',
      required: false,
      placeholder: 'Brief description of this finishing process...',
      rows: 2,
      help: 'Optional description with additional details'
    },
    {
      key: 'category',
      label: 'Category',
      type: 'select',
      required: false,
      options: [
        { value: '', label: 'Select Category...' },
        { value: 'Binding', label: 'Binding' },
        { value: 'Cutting', label: 'Cutting' },
        { value: 'Folding', label: 'Folding' },
        { value: 'Laminating', label: 'Laminating' },
        { value: 'Drilling', label: 'Drilling' },
        { value: 'Perforation', label: 'Perforation' },
        { value: 'Embossing', label: 'Embossing' },
        { value: 'Foiling', label: 'Foiling' },
        { value: 'Die Cutting', label: 'Die Cutting' },
        { value: 'Assembly', label: 'Assembly' },
        { value: 'Packaging', label: 'Packaging' },
        { value: 'General', label: 'General' }
      ],
      help: 'Category for organization and filtering'
    },
    {
      key: 'setup_cost',
      label: 'Setup Cost (£)',
      type: 'number',
      required: false,
      placeholder: '25.00',
      step: '0.01',
      help: 'One-time setup cost for this process'
    },
    {
      key: 'cost_per_sheet',
      label: 'Cost Per Sheet (£)',
      type: 'number',
      required: false,
      placeholder: '0.05',
      step: '0.0001',
      help: 'Cost applied per sheet processed'
    },
    {
      key: 'cost_per_item',
      label: 'Cost Per Item (£)',
      type: 'conditional',
      required: false,
      placeholder: '0.10',
      step: '0.0001',
      help: 'Cost applied per finished item - can be simple price or tiered pricing',
      renderField: (field, value, formData, handleInputChange) => {
        // Check if current value is tiered pricing
        let isTiered = false;
        let tiers = [];
        
        if (typeof value === 'string' && value.trim().startsWith('[')) {
          try {
            tiers = JSON.parse(value);
            isTiered = Array.isArray(tiers) && tiers.length > 0;
          } catch (e) {
            isTiered = false;
          }
        } else if (Array.isArray(value)) {
          tiers = value;
          isTiered = true;
        }
        
        const baseInputClasses = "w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 border-gray-300 focus:border-blue-500";
        
        if (isTiered) {
          // Show tiered pricing summary (read-only for now)
          return (
            <div className="space-y-2">
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                <div className="text-sm font-medium text-blue-800 mb-2">
                  Tiered Pricing ({tiers.length} levels)
                </div>
                <div className="space-y-1 text-xs text-blue-700">
                  {tiers.map((tier, index) => (
                    <div key={index} className="flex justify-between">
                      <span>Qty {tier.min_qty} - {tier.max_qty === 999999 ? '∞' : tier.max_qty}:</span>
                      <span>£{tier.cost.toFixed(4)} each</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="text-xs text-gray-600">
                Note: Tiered pricing can only be modified through database migration scripts currently.
              </div>
            </div>
          );
        } else {
          // Show regular number input
          return (
            <input
              type="number"
              value={value || ''}
              onChange={(e) => handleInputChange(field.key, e.target.value)}
              placeholder={field.placeholder}
              step={field.step || 'any'}
              className={baseInputClasses}
            />
          );
        }
      }
    },
    {
      key: 'cost_per_cut',
      label: 'Cost Per Cut (£)',
      type: 'number',
      required: false,
      placeholder: '0.02',
      step: '0.0001',
      help: 'Cost applied per cut made (for cutting processes)',
      showWhen: (formData) => formData.category && formData.category.toLowerCase() === 'cutting'
    },
    {
      key: 'cutting_stack_capacities',
      label: 'Stack Capacities by Maximum GSM',
      type: 'textarea',
      required: false,
      placeholder: '[{"max_gsm": 100, "sheets_per_stack": 600}]',
      rows: 8,
      help: 'JSON array ordered by maximum GSM. The first band at or above the paper weight is used.',
      showWhen: (formData) => formData.category && formData.category.toLowerCase() === 'cutting'
    },
    {
      key: 'cost_per_stack',
      label: 'Cost Per Stack (£)',
      type: 'number',
      required: false,
      placeholder: '3.00',
      step: '0.01',
      help: 'Cost applied to each stack loaded into the drill',
      showWhen: isPunchProcess
    },
    {
      key: 'punch_stack_capacities',
      label: 'Punch Stack Capacities by Maximum GSM',
      type: 'textarea',
      required: false,
      placeholder: '[{"max_gsm": 100, "sheets_per_stack": 500}]',
      rows: 8,
      help: 'JSON array ordered by maximum GSM. The first band at or above the paper weight is used.',
      showWhen: isPunchProcess
    },
    {
      key: 'time_minutes',
      label: 'Time Required (minutes)',
      type: 'number',
      required: false,
      placeholder: '15',
      step: '1',
      help: 'Estimated time in minutes for this process'
    },
    {
      key: 'requires_setup',
      label: 'Requires Setup',
      type: 'checkbox',
      required: false,
      defaultValue: true,
      help: 'Check if this process requires a setup phase'
    },
    {
      key: 'minimum_quantity',
      label: 'Minimum Quantity',
      type: 'number',
      required: false,
      placeholder: '1',
      step: '1',
      defaultValue: 1,
      help: 'Minimum quantity required for this process'
    },
    {
      key: 'minimum_paper_weight_gsm',
      label: 'Minimum Paper Weight (gsm)',
      type: 'number',
      required: false,
      min: 0,
      step: 'any',
      placeholder: 'e.g. 250',
      help: 'Leave blank when this process has no minimum paper weight'
    },
    {
      key: 'available_for',
      label: 'Available For',
      type: 'multiselect',
      required: false,
      options: [
        { value: 'all', label: 'All Job Types' },
        { value: 'business-cards', label: 'Business Cards' },
        { value: 'cards', label: 'Cards' },
        { value: 'flyers', label: 'Flyers' },
        { value: 'leaflets', label: 'Leaflets' },
        { value: 'booklets', label: 'Booklets' },
        { value: 'brochures', label: 'Brochures' },
        { value: 'letterheads', label: 'Letterheads' },
        { value: 'compliment-slips', label: 'Compliment Slips' },
        { value: 'postcards', label: 'Postcards' },
        { value: 'posters', label: 'Posters' },
        { value: 'banners', label: 'Banners' },
        { value: 'stickers', label: 'Stickers' },
        { value: 'labels', label: 'Labels' },
        { value: 'envelopes', label: 'Envelopes' },
        { value: 'folders', label: 'Folders' },
        { value: 'ncr', label: 'NCR Forms' }
      ],
      defaultValue: ['all'],
      help: 'Job types this process is available for (select "All Job Types" for universal availability)'
    }
  ],

  // Transform functions for API communication
  transformForApi: (data) => {
    const transformed = { ...data };
    
    // Generate ID from name if not provided
    if (transformed.name && !transformed.id) {
      transformed.id = transformed.name.toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '') // Remove special chars except spaces and hyphens
        .replace(/\s+/g, '-') // Replace spaces with hyphens
        .replace(/-+/g, '-') // Replace multiple hyphens with single
        .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
    }
    
    // Ensure numeric fields are properly formatted (except cost_per_item which can be tiered)
    const numericFields = [
      'setup_cost', 'cost_per_sheet', 'cost_per_cut', 'cost_per_stack'
    ];
    numericFields.forEach(field => {
      if (transformed[field] !== undefined && transformed[field] !== '') {
        transformed[field] = parseFloat(transformed[field]) || 0;
      }
    });

    transformed.minimum_paper_weight_gsm = transformed.minimum_paper_weight_gsm === '' ||
      transformed.minimum_paper_weight_gsm == null
      ? null
      : parseFloat(transformed.minimum_paper_weight_gsm);

    if (Array.isArray(transformed.cutting_stack_capacities)) {
      transformed.cutting_stack_capacities = JSON.stringify(
        transformed.cutting_stack_capacities,
        null,
        2
      );
    }

    if (Array.isArray(transformed.punch_stack_capacities)) {
      transformed.punch_stack_capacities = JSON.stringify(
        transformed.punch_stack_capacities,
        null,
        2
      );
    }
    
    // Handle cost_per_item specially - it can be numeric or tiered pricing JSON
    if (transformed.cost_per_item !== undefined && transformed.cost_per_item !== '') {
      // If it's a string that looks like JSON, keep it as-is
      if (typeof transformed.cost_per_item === 'string' && transformed.cost_per_item.trim().startsWith('[')) {
        // Keep as-is, it's already a JSON string
      }
      // If it's an array, stringify it
      else if (Array.isArray(transformed.cost_per_item)) {
        transformed.cost_per_item = JSON.stringify(transformed.cost_per_item);
      }
      // Otherwise treat as numeric
      else {
        transformed.cost_per_item = parseFloat(transformed.cost_per_item) || 0;
      }
    }
    
    const integerFields = ['time_minutes', 'minimum_quantity'];
    integerFields.forEach(field => {
      if (transformed[field] !== undefined && transformed[field] !== '') {
        transformed[field] = parseInt(transformed[field]) || (field === 'minimum_quantity' ? 1 : 0);
      }
    });
    
    // Ensure boolean fields are properly formatted
    if (transformed.requires_setup !== undefined) {
      transformed.requires_setup = Boolean(transformed.requires_setup);
    }
    
    // Handle available_for field
    if (transformed.available_for) {
      if (typeof transformed.available_for === 'string') {
        // Convert semicolon-separated string to array
        transformed.available_for = transformed.available_for.split(';').map(s => s.trim()).filter(s => s);
      }
      // Ensure it's an array
      if (!Array.isArray(transformed.available_for)) {
        transformed.available_for = ['all'];
      }
      // If empty array, default to 'all'
      if (transformed.available_for.length === 0) {
        transformed.available_for = ['all'];
      }
    } else {
      transformed.available_for = ['all'];
    }
    
    return transformed;
  },

  transformFromApi: (data) => {
    // Convert data from API format to form format
    const transformed = { ...data };
    
    // Ensure all numeric fields are properly formatted for forms (except cost_per_item)
    const numericFields = [
      'setup_cost', 'cost_per_sheet', 'cost_per_cut', 'cost_per_stack'
    ];
    numericFields.forEach(field => {
      if (transformed[field] !== null && transformed[field] !== undefined) {
        transformed[field] = parseFloat(transformed[field]);
      }
    });

    transformed.minimum_paper_weight_gsm = transformed.minimum_paper_weight_gsm == null
      ? ''
      : parseFloat(transformed.minimum_paper_weight_gsm);

    if (Array.isArray(transformed.cutting_stack_capacities)) {
      transformed.cutting_stack_capacities = JSON.stringify(
        transformed.cutting_stack_capacities,
        null,
        2
      );
    }


    if (Array.isArray(transformed.punch_stack_capacities)) {
      transformed.punch_stack_capacities = JSON.stringify(
        transformed.punch_stack_capacities,
        null,
        2
      );
    }
    
    // Handle cost_per_item specially - preserve tiered pricing as-is
    if (transformed.cost_per_item !== null && transformed.cost_per_item !== undefined) {
      // If it's a string that looks like JSON, keep it as a string for the form
      if (typeof transformed.cost_per_item === 'string' && transformed.cost_per_item.trim().startsWith('[')) {
        // Keep as-is for the conditional field to detect
      }
      // If it's already an array, keep it as-is
      else if (Array.isArray(transformed.cost_per_item)) {
        // Keep as-is
      }
      // Otherwise treat as numeric
      else {
        transformed.cost_per_item = parseFloat(transformed.cost_per_item);
      }
    }
    
    const integerFields = ['time_minutes', 'minimum_quantity'];
    integerFields.forEach(field => {
      if (transformed[field] !== null && transformed[field] !== undefined) {
        transformed[field] = parseInt(transformed[field]);
      }
    });
    
    // Ensure boolean fields are properly formatted
    if (transformed.requires_setup !== undefined) {
      transformed.requires_setup = Boolean(transformed.requires_setup);
    }
    
    // Ensure available_for is an array
    if (transformed.available_for && !Array.isArray(transformed.available_for)) {
      if (typeof transformed.available_for === 'string') {
        try {
          transformed.available_for = JSON.parse(transformed.available_for);
        } catch (e) {
          transformed.available_for = transformed.available_for.split(';').map(s => s.trim()).filter(s => s);
        }
      } else {
        transformed.available_for = ['all'];
      }
    }
    
    return transformed;
  },

  // Search and filter options
  searchFields: ['name', 'description', 'category'],
  
  // Default sort
  defaultSort: { field: 'category', direction: 'asc' },
  
  // Validation rules
  validation: {
    name: {
      required: true,
      minLength: 2,
      message: 'Process name must be at least 2 characters long'
    },
    setup_cost: {
      min: 0,
      message: 'Setup cost must be a positive number'
    },
    cost_per_sheet: {
      min: 0,
      message: 'Cost per sheet must be a positive number'
    },
    cost_per_item: {
      min: 0,
      message: 'Cost per item must be a positive number'
    },
    cost_per_cut: {
      min: 0,
      message: 'Cost per cut must be a positive number'
    },
    time_minutes: {
      min: 0,
      message: 'Time must be a positive number'
    },
    minimum_quantity: {
      min: 1,
      message: 'Minimum quantity must be at least 1'
    },
    minimum_paper_weight_gsm: {
      min: 0,
      message: 'Minimum paper weight cannot be negative'
    }
  },

  // Additional configuration for special field types
  fieldTypes: {
    multiselect: {
      available_for: {
        placeholder: 'Select job types this process is available for...',
        searchable: true,
        allowSelectAll: true,
        selectAllLabel: 'All Job Types'
      }
    }
  }
};

// Export helper functions
export const transformFinishingProcessForApi = finishingProcessesTableConfig.transformForApi;
export const transformFinishingProcessFromApi = finishingProcessesTableConfig.transformFromApi;
